import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';

interface FlashCardProps {
  front: string;
  back: string;
  className?: string;
  isStacked?: boolean;
  onFlip?: () => void;
  isFlipped?: boolean;
  forceInitialFront?: boolean;
}

/**
 * A flippable card component with enhanced visual effects
 */
const FlashCard: React.FC<FlashCardProps> = ({ 
  front, 
  back, 
  className, 
  isStacked = false,
  onFlip,
  isFlipped = false,
  forceInitialFront = false
}) => {
  // Track hover state for subtle effects
  const [isHovered, setIsHovered] = useState(false);
  // Track whether we're in the middle of a flip animation
  const [isFlipping, setIsFlipping] = useState(false);
  
  // Internal flip state (for controlled or uncontrolled usage)
  const [internalFlipped, setInternalFlipped] = useState(false);
  
  // Determine whether to show front or back based on props or internal state
  const shouldShowFront = isFlipped !== undefined ? !isFlipped : !internalFlipped;
  
  // Handle flip animation completion
  const handleTransitionEnd = () => {
    setIsFlipping(false);
  };
  
  // Handle card click to flip
  const handleCardClick = () => {
    if (isFlipping) return; // Prevent multiple clicks during animation
    
    setIsFlipping(true);
    
    // Update internal state if uncontrolled
    if (isFlipped === undefined) {
      setInternalFlipped(!internalFlipped);
    }
    
    // Call parent handler if provided
    if (onFlip) {
      onFlip();
    }
  };
  
  // When isFlipped prop changes externally, we should reset isFlipping
  useEffect(() => {
    if (isFlipped !== undefined) {
      setIsFlipping(true);
      // Reset flipping state after animation is likely complete
      const timer = setTimeout(() => {
        setIsFlipping(false);
      }, 500); // Matches the transition duration
      
      return () => clearTimeout(timer);
    }
  }, [isFlipped]);
  
  // Set initial state
  useEffect(() => {
    if (forceInitialFront) {
      if (isFlipped !== undefined) {
        // Do nothing - parent controls the state
      } else {
        setInternalFlipped(false);
      }
    }
  }, []);
  
  return (
    <div 
      className={cn(
        "perspective-1000 w-full aspect-[4/3] relative",
        className
      )}
    >
      <div
        className={cn(
          "absolute inset-0 w-full h-full transition-all duration-500 transform-gpu preserve-3d cursor-pointer",
          isFlipped ? "rotate-y-180" : ""
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
        style={{
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* Front face */}
        <div 
          className={cn(
            "absolute inset-0 w-full h-full rounded-xl flex items-center justify-center backface-hidden",
            isStacked && "shadow-xl"
          )}
          style={{ 
            background: 'linear-gradient(145deg, #F5F3FF 0%, #EBE9F5 100%)',
            border: '1.5px solid #D5CDE5',
            boxShadow: isHovered && !isFlipping
              ? '0 8px 20px rgba(106, 90, 205, 0.25), inset 0 1px 3px rgba(255, 255, 255, 0.3)'
              : '0 4px 12px rgba(106, 90, 205, 0.2), inset 0 1px 3px rgba(255, 255, 255, 0.3)',
            backfaceVisibility: "hidden"
          }}
        >
          <span 
            className="text-3xl font-semibold text-center"
            style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
              padding: '16px'
            }}
          >
            {front}
          </span>
        </div>
        
        {/* Back face */}
        <div 
          className={cn(
            "absolute inset-0 w-full h-full rounded-xl flex items-center justify-center backface-hidden",
            isStacked && "shadow-xl"
          )}
          style={{ 
            background: 'linear-gradient(145deg, #DFD8EF 0%, #D5CDE5 100%)',
            border: '1.5px solid #C5BDD5',
            boxShadow: isHovered && !isFlipping
              ? '0 8px 20px rgba(106, 90, 205, 0.25), inset 0 1px 3px rgba(255, 255, 255, 0.3)'
              : '0 4px 12px rgba(106, 90, 205, 0.2), inset 0 1px 3px rgba(255, 255, 255, 0.3)',
            transform: 'rotateY(180deg)', 
            backfaceVisibility: "hidden"
          }}
        >
          <span 
            className="text-3xl font-semibold text-center"
            style={{ 
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
              padding: '16px'
            }}
          >
            {back}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FlashCard;
